import React, { useState, useEffect } from 'react';
// Shared tokens and components load first so page stylesheets (imported by
// the eagerly loaded pages below) can override them.
import './App.css';
import { BrowserRouter, Routes, Route, Navigate, Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider, useQueryClient, focusManager } from '@tanstack/react-query';
import { getSupabase } from './lib/supabase';
import { IntegrationProvider } from './lib/IntegrationContext';
import { BrandProvider } from './lib/BrandContext';
import { useWorkspace, useArticles, qk } from './lib/queries';
import api from './api';
import OnboardingModal from './components/OnboardingModal';
import OnboardingWizard from './components/OnboardingWizard';
import ReportIssue from './components/ReportIssue';
import Icon from './components/Icon';

// TanStack Query pauses a failed request's retry while the tab is hidden. A
// paused query is neither loading nor errored, so pages would fall through to
// their empty state ("No documents yet") for a request that actually failed.
// Nothing here polls or refetches on focus, so report the app as always
// focused: the one retry finishes and pages land on their real error state.
focusManager.setEventListener((setFocused) => {
  setFocused(true);
  return () => {};
});

// Single client for the whole app. 30s staleTime means re-navigating to a
// page within 30s reuses the cached data instantly instead of re-fetching.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,        // data is fresh for 30s
      gcTime:    5 * 60_000,    // keep cached for 5 min after last use
      refetchOnWindowFocus: false,  // don't re-fetch every time the tab regains focus
      retry: 1,
    },
  },
});

class ErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { error: null }; }
  static getDerivedStateFromError(error) { return { error }; }
  render() {
    if (this.state.error) {
      return (
        <div className="page">
          <div className="state-panel is-error" role="alert">
            <div className="state-title">Something went wrong</div>
            <pre style={{ fontFamily: 'var(--gp-mono)', fontSize: 13, lineHeight: '21px', color: 'var(--gp-text-secondary)', whiteSpace: 'pre-wrap', maxWidth: 720 }}>
              {this.state.error.message}
            </pre>
            <button type="button" className="btn btn-secondary" onClick={() => this.setState({ error: null })}>
              Try again
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Dashboard + Login + NewArticle stay eager — they're the landing surfaces
// and the first action a user takes. Everything else is lazy-loaded so it
// doesn't bloat the initial bundle. Drops main.js from ~190 KB gzipped to
// ~90 KB; the heavy pages (Editor, Settings, JiraTickets, BatchProcessor)
// load on demand the first time the user navigates to them.
import Dashboard from './pages/Dashboard';
import NewArticle from './pages/NewArticle';
import Login from './pages/Login';
import NotFound from './pages/NotFound';
import PlanBanner from './components/PlanBanner';
import {
  sessionState, canRenderApp, sessionCopy,
  AUTHENTICATED, UNCONFIGURED, FAILED, INITIALIZING,
} from './lib/sessionState';
const Editor          = React.lazy(() => import('./pages/Editor'));
const UpdateArticle   = React.lazy(() => import('./pages/UpdateArticle'));
const BatchProcessor  = React.lazy(() => import('./pages/BatchProcessor'));
const Settings        = React.lazy(() => import('./pages/Settings'));
const JiraTickets     = React.lazy(() => import('./pages/JiraTickets'));
const FlowConfig      = React.lazy(() => import('./pages/FlowConfig'));
const Publish         = React.lazy(() => import('./pages/Publish'));
const UsageCosts      = React.lazy(() => import('./pages/UsageCosts'));
const Insights        = React.lazy(() => import('./pages/Insights'));
const Reviews         = React.lazy(() => import('./pages/Reviews'));
const ReviewProject   = React.lazy(() => import('./pages/ReviewProject'));

// Minimal Suspense fallback that doesn't flash; the lazy chunks are small
// so most loads are <100 ms after the initial bundle has parsed.
function PageLoading() {
  return (
    <div role="status" className="page meta">Loading…</div>
  );
}

// ── Route wrappers (extract URL params, pass as props) ─────────────────────

function EditorRoute() {
  const { id } = useParams();
  const { state } = useLocation();
  return <Editor articleId={parseInt(id, 10)} initialVideoPath={state?.videoPath || ''} initialJobId={state?.jobId || ''} openPublishOnLoad={!!state?.openPublish} />;
}

function UpdateArticleRoute() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  return (
    <UpdateArticle
      articleId={parseInt(id, 10)}
      contextNote={state?.contextNote || ''}
      onDone={doneId => navigate(`/editor/${doneId}`)}
      onCancel={() => navigate('/dashboard')}
    />
  );
}

function NewArticleRoute() {
  const { state } = useLocation();
  const navigate = useNavigate();
  return (
    <NewArticle
      prefill={state?.prefill || null}
      // When New Review is launched from inside a project, the project ref
      // travels in route state — NewArticle forwards it to FigmaImportPanel
      // so the import call attaches the review to that project.
      project={state?.project || null}
      onCreated={(id, videoPath, jobId) => navigate(`/editor/${id}`, { state: { videoPath, jobId } })}
    />
  );
}

function DashboardRoute() {
  const navigate = useNavigate();
  return (
    <Dashboard
      onEdit={id => navigate(`/editor/${id}`)}
      onUpdate={(id, contextNote) => navigate(`/update/${id}`, { state: { contextNote } })}
      onNew={() => navigate('/new')}
    />
  );
}

function BatchRoute() {
  const navigate = useNavigate();
  return <BatchProcessor onEdit={id => navigate(`/editor/${id}`)} />;
}

// ── Sidebar + main shell ───────────────────────────────────────────────────

// hideFor: persona on which to hide this item ('design' or 'product')
// labelFor: persona-specific label override (e.g., New Article → New Review)
// Sidebar items either navigate to a path or trigger an action (sentinel
// string the AppShell maps to a handler — e.g. opening a drawer). Items
// also support persona gating via hideFor; groups may have showFor to
// restrict the entire group to a single persona. An item may carry an
// optional state object that is passed as React Router location.state on
// navigate — used by "+ New Review" to signal review intent to /new.
// Sidebar mirrors the pipeline diagram 1:1 — one item per stage of the
// Design → Approve/Build → Test → Publish → Support cycle, plus the /flow
// configurator (FC-01) that draws it.
//
// Group headers were removed: each header restated the stage name that its
// single item already carried ("PUBLISH › Publish", "SUPPORT › Insights"),
// so the nav read as roughly three times longer than the workflow it
// describes. Creation actions ("New Review", "New Doc", "New Project") are
// no longer nav items either — they live on the page that owns them, which
// already renders them. Two of them also shared one route (/new), which made
// the active-item state ambiguous and unaddressable by URL.
const NAV_ITEMS = [
  { path: '/flow',      icon: 'flow',    label: 'Flow' },
  { path: '/reviews',   icon: 'design',  label: 'Design' },
  { path: '/jira',      icon: 'build',   label: 'Build & Test' },
  { path: '/dashboard', icon: 'publish', label: 'Publish' },
  { path: '/insights',  icon: 'support', label: 'Support' },
];

// Account items render in the sidebar footer, away from the pipeline, so the
// main nav is nothing but the workflow.
const ACCOUNT_ITEMS = [
  { path: '/usage',    icon: 'usage',    label: 'Usage & Costs' },
  { path: '/settings', icon: 'settings', label: 'Settings' },
];

function navItemsForPersona(items, useCase) {
  return items
    .filter(item => !(item.hideFor && item.hideFor === useCase))
    .map(item => ({
      ...item,
      label: (item.labelFor && item.labelFor[useCase]) || item.label,
    }));
}

// Fetches /api/bootstrap once on mount and seeds TanStack Query cache so the
// individual useWorkspace / useBrandStyle / useBrandOnboarded / useDetailLevel
// hooks return synchronously on first render. Cuts cold first-paint by 4-6
// roundtrips. Mounts inside QueryClientProvider so useQueryClient is available.
function BootstrapPrefill() {
  const queryClient = useQueryClient();
  React.useEffect(() => {
    let cancelled = false;
    api.get('/bootstrap').then(r => {
      if (cancelled) return;
      const b = r.data || {};
      if (b.workspace)      queryClient.setQueryData(qk.workspace(),      b.workspace);
      if (b.brand_style)    queryClient.setQueryData(qk.brandStyle(),    b.brand_style);
      if (b.brand_voice)    queryClient.setQueryData(qk.brandVoice(),    b.brand_voice);
      if ('brand_onboarded' in b) queryClient.setQueryData(qk.brandOnboarded(), b.brand_onboarded);
      if (b.detail_level)   queryClient.setQueryData(qk.detailLevel(),   b.detail_level);
    }).catch(() => { /* graceful — individual hooks will re-fetch */ });
    return () => { cancelled = true; };
  }, [queryClient]);
  return null;
}


function AppShell({ user, onSignOut, theme, setTheme }) {
  const navigate  = useNavigate();
  const location  = useLocation();
  const { data: workspace } = useWorkspace();
  const { data: articles = [] } = useArticles();
  const useCase = workspace?.use_case;
  const navItems       = navItemsForPersona(NAV_ITEMS, useCase);
  const accountItems   = navItemsForPersona(ACCOUNT_ITEMS, useCase);
  const showOnboarding = workspace != null && useCase == null;

  const [confirmSignOut, setConfirmSignOut] = useState(false);

  // Determine the active nav item. Every item is now a plain path, so the
  // key is just that path.
  const _allNavItems = [...navItems, ...accountItems];
  const activeKey = (() => {
    const path = location.pathname;
    if (path === '/' || path === '/dashboard') return '/dashboard';
    // /new serves both creation flows. ?review=1 is the addressable signal
    // (legacy location.state is still honoured for in-app navigations that
    // haven't been updated); it decides which stage stays lit.
    if (path === '/new') {
      const isReview = new URLSearchParams(location.search).get('review') === '1'
        || !!location.state?.reviewFlow;
      return isReview ? '/reviews' : '/dashboard';
    }
    // /editor/:id — highlight Reviews for design_review articles, Publish for docs.
    const editorMatch = path.match(/^\/editor\/(\d+)$/);
    if (editorMatch) {
      const aid = parseInt(editorMatch[1], 10);
      const art = articles.find(a => a.id === aid);
      return art?.use_case === 'design_review' ? '/reviews' : '/dashboard';
    }
    // Otherwise pick the longest item.path prefix that matches (handles
    // /reviews/:slug, etc.).
    const sorted = [..._allNavItems].sort((a, b) => (b.path?.length || 0) - (a.path?.length || 0));
    const match = sorted.find(item => item.path && path.startsWith(item.path));
    // F08 — no match means no section. Falling back to /dashboard lit
    // "Publish" on a page that isn't in it, so the nav and the page title
    // disagreed. Nothing is highlighted on a not-found page.
    return match ? match.path : null;
  })();

  // Below 768px the sidebar becomes a modal drawer opened from the topbar.
  // It closes on navigation, on Escape and on backdrop click, and returns
  // focus to the menu button.
  const [navOpen, setNavOpen] = useState(false);
  const menuBtnRef = React.useRef(null);
  const sidebarRef = React.useRef(null);
  const closeNav = React.useCallback((restoreFocus = true) => {
    setNavOpen(false);
    if (restoreFocus) menuBtnRef.current?.focus();
  }, []);
  useEffect(() => { setNavOpen(false); }, [location.pathname]);
  useEffect(() => {
    if (!navOpen) return undefined;
    const panel = sidebarRef.current;
    panel?.querySelector('a, button')?.focus();
    const onKey = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); closeNav(); return; }
      if (e.key !== 'Tab' || !panel) return;
      const items = [...panel.querySelectorAll('a[href], button:not([disabled])')];
      const first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [navOpen, closeNav]);

  const renderNavItem = (item) => (
    <li key={item.path}>
      <Link
        to={item.path}
        className="nav-btn"
        aria-current={activeKey === item.path ? 'page' : undefined}
      >
        <span className="nav-icon"><Icon name={item.icon} /></span>
        {item.label}
      </Link>
    </li>
  );

  const brand = (
    <Link to="/dashboard" className="logo" aria-label="Flow by Affixly — Publish">
      <div className="logo-mark" aria-hidden="true">AF</div>
      <div>
        <div className="logo-name">Flow</div>
        <div className="logo-sub">by Affixly</div>
      </div>
    </Link>
  );

  return (
    <div className="app">
      {showOnboarding && <OnboardingModal />}

      <header className="topbar">
        {brand}
        <button
          ref={menuBtnRef}
          type="button"
          className="icon-btn"
          aria-label="Open navigation"
          aria-expanded={navOpen}
          aria-controls="app-nav"
          onClick={() => setNavOpen(true)}
        >
          <Icon name="menu" />
        </button>
      </header>
      <div className={`nav-backdrop ${navOpen ? 'is-open' : ''}`} onClick={() => closeNav()} aria-hidden="true" />

      <nav
        id="app-nav"
        ref={sidebarRef}
        className={`sidebar ${navOpen ? 'is-open' : ''}`}
        aria-label="Main"
      >
        <div className="sidebar-head">
          {brand}
          <button type="button" className="icon-btn drawer-close" aria-label="Close navigation" onClick={() => closeNav()}>
            <Icon name="close" />
          </button>
        </div>
        <div className="nav-scroll">
          <ul className="nav-list">
            {navItems.map(renderNavItem)}
          </ul>
          <ul className="nav-list nav-list-account">
            {accountItems.map(renderNavItem)}
          </ul>
        </div>
        <div className="sidebar-footer">
          {user && <div className="sidebar-user" title={user.email}>{user.email}</div>}
          <div className="sidebar-footer-row">
            <span className="version-badge">v1.0</span>
            <div className="theme-toggle" role="group" aria-label="Theme">
              <button
                type="button"
                className="theme-toggle-btn"
                aria-pressed={theme === 'dark'}
                onClick={() => setTheme('dark')}
              >Dark</button>
              <button
                type="button"
                className="theme-toggle-btn"
                aria-pressed={theme === 'light'}
                onClick={() => setTheme('light')}
              >Light</button>
            </div>
          </div>
          {user && (
            confirmSignOut ? (
              <div className="signout-confirm">
                <p>Sign out of Flow?</p>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="btn btn-secondary btn-sm" style={{ flex: 1 }} onClick={onSignOut}>
                    Sign out
                  </button>
                  <button type="button" className="btn btn-ghost btn-sm" style={{ flex: 1 }} onClick={() => setConfirmSignOut(false)}>
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <button type="button" className="btn btn-ghost btn-sm btn-block" onClick={() => setConfirmSignOut(true)}>
                <Icon name="signOut" /> Sign out
              </button>
            )
          )}
        </div>
      </nav>

      <main className="main-content">
        {/* Onboarding banner — rendered above the route content. Combined with
            the wizard's own once-per-session suppression, this means it shows
            on whichever page the user first lands and never re-surfaces while
            they navigate around within the session. */}
        {/* §1.3.3 — the 80% warning and the read-only banner. Silent until
            an account is actually near a limit, so this is safe with
            PLANS_ENFORCED off. */}
        <PlanBanner />
        <OnboardingWizard />
        <ErrorBoundary key={location.pathname}>
          <React.Suspense fallback={<PageLoading />}>
            <Routes>
              <Route path="/"            element={<DashboardRoute />} />
              <Route path="/flow"        element={<FlowConfig />} />
              <Route path="/dashboard"   element={<DashboardRoute />} />
              <Route path="/new"         element={<NewArticleRoute />} />
              <Route path="/editor/:id"  element={<EditorRoute />} />
              <Route path="/update/:id"  element={<UpdateArticleRoute />} />
              <Route path="/batch"       element={<BatchRoute />} />
              {/* GP-24 — Support folded into Insights */}
              <Route path="/support"     element={<Navigate to="/insights" replace />} />
              <Route path="/insights"    element={<Insights />} />
              <Route path="/reviews"          element={<Reviews />} />
              <Route path="/reviews/:slug"    element={<ReviewProject />} />
              {/* GP-23 — Docs + Publish merged onto /dashboard */}
              <Route path="/publish"     element={<Navigate to="/dashboard" replace />} />
              <Route path="/wiki"        element={<Publish />} />
              <Route path="/jira"        element={<JiraTickets />} />
              <Route path="/usage"       element={<UsageCosts />} />
              <Route path="/settings"    element={<Settings />} />
              {/* Fallback */}
              {/* F08 — an unknown path says so instead of quietly rendering
                  the dashboard. The redirects above are intentional aliases
                  and still work. */}
              <Route path="*"            element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </ErrorBoundary>
      </main>


      {/* GP-21 — global "Report an issue" (floating button + ⌘⇧R), app-wide */}
      <ReportIssue />
    </div>
  );
}

// ── Root ───────────────────────────────────────────────────────────────────

export default function App() {
  const [theme, setTheme]       = useState(() => {
    const t = localStorage.getItem('gp_theme');
    return t === 'light' ? 'light' : 'dark';  // whitelist valid values
  });
  const [user, setUser]         = useState(null);
  // X01 — the session lookup has three outcomes, not two: settled with a
  // user, settled without one, and failed. Collapsing the third into
  // "not ready" is what produced a permanently blank page.
  const [authSettled, setAuthSettled] = useState(false);
  const [authError, setAuthError]     = useState(null);
  const [authAttempt, setAuthAttempt] = useState(0);   // bumped by Retry

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('gp_theme', theme);
  }, [theme]);

  useEffect(() => {
    const client = getSupabase();
    if (!client) {
      // Not "ready with no user" — genuinely unconfigured. sessionState()
      // keeps that distinct so a broken production deploy cannot look like
      // an ordinary signed-out visitor.
      setAuthSettled(true);
      return undefined;
    }
    // X01 — ignore a response that lands after unmount or after a Retry has
    // superseded this attempt.
    let cancelled = false;
    setAuthError(null);
    setAuthSettled(false);

    client.auth.getSession()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error) { setAuthError(error); return; }
        setUser(data?.session?.user ?? false);
      })
      .catch((err) => {
        // The missing branch. Without it a rejection left authReady false
        // forever and the app rendered nothing at all.
        if (!cancelled) setAuthError(err || new Error('Session check failed'));
      })
      .finally(() => { if (!cancelled) setAuthSettled(true); });

    const { data: { subscription } } = client.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      setUser(session?.user ?? false);
      setAuthError(null);          // a live auth event means the channel works
    });
    return () => { cancelled = true; subscription.unsubscribe(); };
  }, [authAttempt]);

  const handleSignOut = async () => {
    const client = getSupabase();
    if (client) await client.auth.signOut();
    // Local drafts survive sign-out here by design — mayClearLocalDrafts()
    // documents that only a deliberate sign-out could clear them, and Flow's
    // stated behaviour is to keep a draft on the device that made it.
  };

  // X01 — resolve the session into one of five states and render each one
  // deliberately. None of them is a blank page.
  const isProduction = process.env.NODE_ENV === 'production';
  const authState = sessionState({
    hasClient: Boolean(getSupabase()),
    isProduction,
    settled: authSettled,
    user,
    error: authError,
  });

  if (authState === INITIALIZING) {
    return (
      <div className="page" role="status" aria-live="polite" style={{ padding: 32 }}>
        {sessionCopy(INITIALIZING).title}
      </div>
    );
  }

  if (authState === FAILED || (authState === UNCONFIGURED && isProduction)) {
    const copy = sessionCopy(authState, { isProduction });
    return (
      <div className="page" style={{ padding: 32, maxWidth: 560 }}>
        <div className="inline-msg is-error" role="alert">
          <div style={{ flex: 1 }}>
            <strong>{copy.title}</strong>
            <p style={{ marginTop: 4 }}>{copy.body}</p>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
              {copy.retry && (
                <button type="button" className="btn btn-primary"
                        onClick={() => setAuthAttempt(n => n + 1)}>
                  {copy.retry}
                </button>
              )}
              {copy.signIn && (
                <button type="button" className="btn btn-secondary"
                        onClick={async () => { await handleSignOut(); setAuthAttempt(n => n + 1); }}>
                  {copy.signIn}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!canRenderApp(authState, { isProduction })) return <Login />;

  return (
    <QueryClientProvider client={queryClient}>
      <BootstrapPrefill />
      <BrowserRouter>
        <IntegrationProvider>
          <BrandProvider>
            <AppShell
              user={user}
              onSignOut={handleSignOut}
              theme={theme}
              setTheme={setTheme}
            />
          </BrandProvider>
        </IntegrationProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
